import { Product, Product_TallyDimension } from "gen/product/models_pb"
import { TallyRow } from "gen/txn/models_pb"
import { Decimal } from "shared-ts/classes/decimal"

import { FormErrors } from "hooks/useForm"
import { useDecimalQuantitiesOperations } from "lib/decimal/useDecimalQuantitiesOperations"

import {
  areTallyRowsEqual,
  emptyTallyRow,
  isTallyBlank,
  validateTallyRows,
} from "./tally.helpers"

import type { TallyFormData } from "./tally.types"

export interface useTallySectionRes {
  defaults: TallyFormData
  showTallySection: boolean
  dimension: Product_TallyDimension
  lengthDecimalPlaces: number
  tallyEntryDenominator: number
  eq: (a: TallyFormData, b: TallyFormData) => boolean
  validate: (values: TallyFormData) => FormErrors<TallyFormData>
}

interface useTallySectionArgs {
  product: Product
  tallyRows: TallyRow[]
  snapshotDimension: Product_TallyDimension
  snapshotTallyEntryDenominator: number
  readOnly: boolean
  uomCode?: string
  maxTotal?: Decimal
  isSaleKit: boolean
  allowNegativeQuantity?: boolean
}

export function useTallySection({
  product,
  tallyRows,
  snapshotDimension,
  snapshotTallyEntryDenominator,
  readOnly,
  uomCode,
  maxTotal,
  isSaleKit,
  allowNegativeQuantity = false,
}: useTallySectionArgs): useTallySectionRes {
  const hasSnapshot = snapshotDimension !== Product_TallyDimension.UNDEFINED
  const dimension = hasSnapshot ? snapshotDimension : product.tallyDimension
  const entryDenominator = hasSnapshot
    ? snapshotTallyEntryDenominator
    : product.tallyEntryDenominator
  const showTallySection =
    !isSaleKit &&
    (readOnly
      ? tallyRows.length > 0
      : dimension !== Product_TallyDimension.UNDEFINED)

  const allowDecimalQuantities = !!product.allowDecimalQuantities
  const { numberDecimalPlaces } = useDecimalQuantitiesOperations()
  const lengthDecimalPlaces = allowDecimalQuantities ? numberDecimalPlaces : 0

  let defaultRows: TallyRow[] = []
  if (showTallySection && tallyRows.length > 0) {
    defaultRows = tallyRows
  } else if (showTallySection && !readOnly) {
    defaultRows = [emptyTallyRow(dimension)]
  }

  const validate = (values: TallyFormData): FormErrors<TallyFormData> => {
    if (!showTallySection || readOnly) {
      return {}
    }
    // A blank tally is "no tally"
    if (isTallyBlank(values.tallyRows)) {
      return {}
    }
    return validateTallyRows(
      values.tallyRows,
      dimension,
      allowDecimalQuantities,
      entryDenominator,
      maxTotal,
      uomCode,
      allowNegativeQuantity
    )
  }

  return {
    defaults: {
      tallyRows: defaultRows,
      tallyDimension: dimension,
      tallyEntryDenominator: entryDenominator,
    },
    showTallySection,
    dimension,
    lengthDecimalPlaces,
    tallyEntryDenominator: entryDenominator,
    eq: (a, b) => areTallyRowsEqual(a.tallyRows, b.tallyRows),
    validate,
  }
}
